import React, { useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";
import "./debt.less";
import { Invit } from "../../../../types/invit.types";

interface DebtProps {
  invit: Invit | null;
}

interface BarData {
  year: string;
  amount: number;
}

const Debt: React.FC<DebtProps> = ({ invit }) => {
  if (!invit) return null;

  const chartRef = useRef<SVGSVGElement | null>(null);

  // --- Prepare data for D3 ---
  const currentData: BarData[] = useMemo(() => {
    if (!invit?.debtMaturityProfile) return [];

    const graphProp = invit.debtMaturityProfile.graphProp;
    if (!graphProp) return [];

    const data: BarData[] = [
      {
        year: graphProp.lessThanOneYears.key,
        amount: Number(graphProp.lessThanOneYears.value) || 0,
      },
      {
        year: graphProp.oneToFiveYears.key,
        amount: Number(graphProp.oneToFiveYears.value) || 0,
      },
      {
        year: graphProp.moreThanFiveYears.key,
        amount: Number(graphProp.moreThanFiveYears.value) || 0,
      },
    ];

    return data;
  }, [invit]);

  // --- D3 Chart Rendering ---
  useEffect(() => {
    if (!chartRef.current || !currentData.length) return;

    const renderChart = () => {
      if (!chartRef.current) return;

      const margin = { top: 20, right: 20, bottom: 60, left: 90 };
      const width = chartRef.current.clientWidth - margin.left - margin.right;
      const height = 350;

      const bottomGap = 10;

      const svg = d3
        .select(chartRef.current)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);

      svg.selectAll("*").remove();

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // X Scale
      const x = d3
        .scaleBand()
        .domain(currentData.map((d) => d.year))
        .range([0, width])
        .padding(0.6);

      // Y Scale
      const y = d3
        .scaleLinear()
        .domain([0, d3.max(currentData, (d) => d.amount)! * 1.1])
        .nice()
        .range([height, 40]);

      // Grid Lines
      g.append("g")
        .attr("class", "grid")
        .lower()
        .call(
          d3
            .axisLeft(y)
            .tickSize(-width)
            .tickFormat(() => "")
        )
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".tick line")
            .attr("stroke", "#D9E1EC")
            .attr("stroke-dasharray", "2,4")
        );

      // remove baseline grid line
      g.select(".grid")
        .selectAll(".tick")
        .filter((d) => d === 0)
        .remove();

      // Bars
      g.append("g")
        .attr("class", "bars")
        .selectAll("rect")
        .data(currentData)
        .enter()
        .append("rect")
        .attr("x", (d) => x(d.year)!)
        .attr("y", height)
        .attr("width", x.bandwidth())
        .attr("height", 0)
        .attr("rx", 7)
        .attr("fill", "#14F2E73D")
        .transition()
        .duration(800)
        .attr("y", (d) => y(d.amount) - bottomGap)
        .attr("height", (d) => height - y(d.amount));

      // X Axis
      g.append("g")
        .attr("transform", `translate(0, ${height})`)
        .call(d3.axisBottom(x).tickSize(0))
        .call((g) =>
          g
            .select(".domain")
            .attr("stroke", "#5A607F30")
            .attr("stroke-width", 1)
        )
        .selectAll("text")
        .style("font-size", "14px")
        .attr("dy", "1.5em")
        .style("font-family", "Duplet")
        .style("fill", "#5A607F");

      g.append("text")
        .attr("x", width / 2)
        .attr("y", height + 55)
        .attr("text-anchor", "middle")
        .attr("font-family", "Duplet")
        .attr("font-size", "15px")
        .attr("font-weight", "bold")
        .style("fill", "#132C49")
        .text("Maturity Buckets");

      // Y Axis
      g.append("g")
        .call(d3.axisLeft(y).ticks(6).tickSize(0))
        .call((g) =>
          g
            .select(".domain")
            .attr("stroke", "#5A607F30")
            .attr("stroke-width", 1)
        )
        .call((g) =>
          g
            .selectAll(".tick text")
            .style("font-size", "14px")
            .attr("dx", "-0.7em")
            .style("font-family", "Duplet")
            .style("fill", "#5A607F")
        );

      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -(height / 2))
        .attr("y", -65)
        .attr("text-anchor", "middle")
        .attr("font-family", "Duplet")
        .attr("font-size", "15px")
        .attr("font-weight", "bold")
        .style("fill", "#132C49")
        .text("Amount (₹ Mn.)");

      // Hover Effects
      g.selectAll(".bars rect")
        .on("mouseover", function () {
          d3.select(this)
            .transition()
            .duration(200)
            .attr("fill", "#14F2E7")
            .style("filter", "drop-shadow(0px 9px 11.2px rgba(19,44,73,0.2))");
        })
        .on("mouseout", function () {
          d3.select(this)
            .transition()
            .duration(200)
            .attr("fill", "#14F2E73D")
            .style("filter", "none");
        });
    };

    renderChart();
    window.addEventListener("resize", renderChart);
    return () => window.removeEventListener("resize", renderChart);
  }, [currentData]);

  return (
    <section className="debt-maturity">
      <div className="debt-maturity__card">
        <div className="debt-maturity__header">
          <h2>Debt Maturity Profile</h2>
        </div>

        <div className="debt-maturity__body">
          <div className="debt-maturity__chart-box">
            <div className="debt-maturity__chart-area">
              <svg ref={chartRef} />
            </div>
          </div>

          <div className="debt-maturity__note">
            <p>
              Note: The data shows distribution of borrowings by maturity
              buckets.
            </p>
          </div>
        </div>

        <div className="debt-maturity__table">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                {currentData.map((d, idx) => (
                  <th key={idx}>{d.year}</th>
                ))}
                <th>Total Borrowing</th>
                <th>Carrying Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cashflows (₹ Mn.)</td>
                {currentData.map((d, idx) => (
                  <td key={idx}>{d.amount.toLocaleString()}</td>
                ))}
                <td>
                  {Number(
                    invit.debtMaturityProfile?.totalBorrowing?.value ?? 0
                  ).toLocaleString()}
                </td>
                <td>
                  {Number(
                    invit.debtMaturityProfile?.graphProp?.carryingValue
                      ?.value ?? 0
                  ).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Debt;
